import { TOPFAN_HEADER_DEFAULT_LOGO_URLS } from '@constants/constant';
import {
  buildHeaderLayoutData,
  HEADER_LAYOUT_CONSTANTS
} from '@modules/profile/component/marketing-page-layout-container/header-layout/header-layout.constants';
import type { HeaderLayoutData } from '@modules/profile/component/marketing-page-layout-container/header-layout/header-layout.interface';

export const HEADER_HOST_LABELS = {
  PROFILE_IMAGE_ALT: 'Profile Image'
} as const;

export const AUTH_HEADER_PATH_PREFIXES = ['/auth'] as const;

export const LEGAL_INFORMATIVE_PATH_PREFIXES = [
  '/privacy-policy',
  '/terms-conditions',
  '/terms-of-service',
  '/contact-us',
  '/request-demo',
  '/schedule-a-demo',
  '/about',
  '/pricing',
  '/help-faq',
  '/unsubscribe-instruction',
  '/unsubscribe/newsletter',
  '/imported/opt-out'
] as const;

export const TOPFAN_HEADER_LOGO_VARIANT = {
  PRO: 'pro',
  DARK: 'dark',
  LIGHT: 'light'
} as const;

export const TOPFAN_HEADER_CONTENT = {
  LOGO_URLS: {
    DARK: TOPFAN_HEADER_DEFAULT_LOGO_URLS.MARKETING_HOME_FALLBACK,
    LIGHT: TOPFAN_HEADER_DEFAULT_LOGO_URLS.DEFAULT,
    PRO: TOPFAN_HEADER_DEFAULT_LOGO_URLS.PRO_PAGE
  },
  CTAS: {
    SIGN_IN: {
      text: HEADER_LAYOUT_CONSTANTS.SIGN_IN_LABEL,
      link: HEADER_LAYOUT_CONSTANTS.SIGN_IN_LINK
    },
    CREATE_ACCOUNT: {
      text: HEADER_LAYOUT_CONSTANTS.CREATE_ACCOUNT_LABEL,
      link: HEADER_LAYOUT_CONSTANTS.CREATE_ACCOUNT_LINK
    }
  }
} as const;

export const TOPFAN_PRO_HOME_PATH = '/pro';

export function isTopFanProHomePath(path: string): boolean {
  return path === TOPFAN_PRO_HOME_PATH;
}

export function resolveTopFanHeaderLogoVariant(options: {
  isAtProHomePage: boolean;
  isPromotionalHome: boolean;
  isAtHomePage: boolean;
}): (typeof TOPFAN_HEADER_LOGO_VARIANT)[keyof typeof TOPFAN_HEADER_LOGO_VARIANT] {
  if (options.isAtProHomePage) {
    return TOPFAN_HEADER_LOGO_VARIANT.PRO;
  }
  if (options.isPromotionalHome || options.isAtHomePage) {
    return TOPFAN_HEADER_LOGO_VARIANT.DARK;
  }
  return TOPFAN_HEADER_LOGO_VARIANT.LIGHT;
}

export function resolveTopFanHeaderLogoUrl(options: {
  isAtProHomePage: boolean;
  isPromotionalHome: boolean;
  isAtHomePage: boolean;
}): string {
  const variant = resolveTopFanHeaderLogoVariant(options);
  if (variant === TOPFAN_HEADER_LOGO_VARIANT.PRO) {
    return TOPFAN_HEADER_CONTENT.LOGO_URLS.PRO;
  }
  if (variant === TOPFAN_HEADER_LOGO_VARIANT.DARK) {
    return TOPFAN_HEADER_CONTENT.LOGO_URLS.DARK;
  }
  return TOPFAN_HEADER_CONTENT.LOGO_URLS.LIGHT;
}

export function getTopFanHeaderLayoutData(
  logoUrl: string = TOPFAN_HEADER_CONTENT.LOGO_URLS.DARK
): HeaderLayoutData {
  return buildHeaderLayoutData({
    ctaText: TOPFAN_HEADER_CONTENT.CTAS.SIGN_IN.text,
    ctaLink: TOPFAN_HEADER_CONTENT.CTAS.SIGN_IN.link,
    ctaText2: TOPFAN_HEADER_CONTENT.CTAS.CREATE_ACCOUNT.text,
    ctaLink2: TOPFAN_HEADER_CONTENT.CTAS.CREATE_ACCOUNT.link,
    logoUrl
  });
}

export function getActiveHeaderLayoutData(options: {
  isWhiteLabel: boolean;
  isAtProHomePage?: boolean;
  isPromotionalHome?: boolean;
  isAtHomePage?: boolean;
  whiteLabelLogoUrl?: string;
}): HeaderLayoutData {
  if (options.isWhiteLabel) {
    return buildHeaderLayoutData({
      logoUrl: options.whiteLabelLogoUrl
    });
  }
  return getTopFanHeaderLayoutData(
    resolveTopFanHeaderLogoUrl({
      isAtProHomePage: !!options.isAtProHomePage,
      isPromotionalHome: !!options.isPromotionalHome,
      isAtHomePage: !!options.isAtHomePage
    })
  );
}
